import React, { useState } from 'react';
import { ErrorNotice, Spinner } from '../ui';
import { schoolLevelOfGrade } from '../periodDates';
import FloatingScrollArea from '../../common/FloatingScrollArea';

const LEVELS = [['ELEM', '초등'], ['MIDDLE', '중등'], ['HIGH', '고등'], ['OTHER', '기타']];
const levelOf = (key) => key == null ? 'OTHER' : schoolLevelOfGrade(Number(key)) || 'OTHER';
const activeCount = (row) => row.count - (row.paused || 0);

/** 학생 학년으로 나눈 학교별 인원. 미지정·졸업·비재학도 빼지 않고, 모든 학교를 스크롤로 확인한다. */
export default function SchoolGradeTable({ groups = [], levelValue, onLevelChange, onOpenDetail, compact = false, loading = false, error, onRetry }) {
  const counts = Object.fromEntries(LEVELS.map(([key]) => [key, groups.reduce((sum, group) => sum
    + group.splits.filter((split) => levelOf(split.key) === key).reduce((n, split) => n + activeCount(split), 0), 0)]));
  const [localLevel, setLocalLevel] = useState(null);
  const level = ['ALL', ...LEVELS.map(([key]) => key)].includes(levelValue) ? levelValue
    : localLevel || LEVELS.find(([key]) => counts[key] > 0)?.[0] || 'ELEM';
  const setLevel = (value) => { setLocalLevel(value); onLevelChange?.(value); };
  const allCount = groups.reduce((sum, group) => sum + activeCount(group), 0);
  const tabs = [
    ['ALL', '전체', allCount],
    ...LEVELS.filter(([key]) => key !== 'OTHER' || counts.OTHER > 0 || level === 'OTHER')
      .map(([key, label]) => [key, label, counts[key]]),
  ];
  const rows = groups.map((group) => {
    const splits = group.splits.filter((split) => level === 'ALL' || levelOf(split.key) === level);
    return { ...group, splits, total: level === 'ALL' ? activeCount(group) : splits.reduce((sum, split) => sum + activeCount(split), 0) };
  }).filter((group) => group.total > 0)
    .sort((a, b) => b.total - a.total || a.label.localeCompare(b.label, 'ko'));
  const columns = new Map();
  rows.forEach((group) => group.splits.forEach((split) => {
    const key = split.key == null ? 'unknown' : String(split.key);
    if (!columns.has(key)) columns.set(key, { key, label: split.key == null ? '미지정' : split.label });
  }));
  const gradeCols = [...columns.values()].sort((a, b) => (a.key === 'unknown' ? 99 : Number(a.key)) - (b.key === 'unknown' ? 99 : Number(b.key)));
  const cell = (group, key) => {
    const split = group.splits.find((value) => (value.key == null ? 'unknown' : String(value.key)) === key);
    return split ? activeCount(split) || '' : '';
  };

  return (
    <section className={`min-w-0 rounded-2xl border border-gray-200 bg-white ${compact ? 'space-y-1 p-2' : 'space-y-2 p-3'}`}>
      <div className="flex min-w-0 items-center gap-2">
        <h4 className="shrink-0 text-sm font-semibold text-gray-900">학교 × 학년</h4>
        <div role="group" aria-label="학교급 선택" className="ml-auto flex min-w-0 gap-1 overflow-x-auto [scrollbar-width:none]">
          {tabs.map(([key, label, count]) => (
            <button key={key} type="button" aria-pressed={level === key} aria-label={`${label} ${count}명`} title={`${label} ${count}명`}
              onClick={() => setLevel(key)}
              className={`h-5 shrink-0 whitespace-nowrap rounded-full px-1.5 text-[10px] leading-5 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary ${level === key ? 'bg-primary font-semibold text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div className={compact ? 'flex h-6 min-w-0 items-center justify-between gap-x-2' : 'space-y-2'}>
        <p className="min-w-0 truncate text-[11px] text-gray-500">학생 학년 기준 · 휴원 제외 · {rows.filter((row) => row.key != null).length}개 학교 · {level === 'ALL' ? allCount : counts[level]}명</p>
        {onOpenDetail && <button type="button" aria-label="학교별 통계 자세히" onClick={() => onOpenDetail(level)} className="min-h-6 shrink-0 text-xs text-blue-700 hover:underline focus:outline-none focus-visible:ring-2 focus-visible:ring-primary">{compact ? '자세히' : '학교별 통계 자세히'}</button>}
      </div>
      <FloatingScrollArea className={`rounded-lg border border-gray-100 ${compact ? 'h-32' : 'h-64'}`} role="region" aria-label="학교별 학년 인원" aria-busy={loading}>
        {error ? <ErrorNotice error={error} onRetry={onRetry} compact /> : loading ? <Spinner label="학교별 현황 불러오는 중" compact /> : rows.length === 0 ? <p className="py-2 text-center text-xs text-gray-500">해당 학교급의 재원생이 없습니다.</p> : (
          <table className={`w-full table-fixed text-xs ${compact ? '[&_td]:py-1 [&_th]:py-1' : ''}`} style={{ minWidth: compact ? Math.max(280, 128 + gradeCols.length * 40 + 48) : Math.max(360, 160 + gradeCols.length * 44 + 48) }}>
            <thead className="sticky top-0 z-10 bg-gray-50">
              <tr className="text-gray-500">
                <th scope="col" className={`${compact ? 'w-32' : 'w-40'} p-2 text-left font-medium`}>학교</th>
                {gradeCols.map((column) => <th scope="col" key={column.key} className="p-2 text-right font-medium">{column.label}</th>)}
                <th scope="col" className="w-12 p-2 text-right font-medium">합</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((group) => (
                <tr key={group.key ?? 'none'} className="border-t border-gray-100">
                  <th scope="row" className={`break-words p-2 text-left font-normal ${group.key == null ? 'text-gray-400' : 'text-gray-900'}`}>{group.label}</th>
                  {gradeCols.map((column) => <td key={column.key} className="p-2 text-right text-gray-700">{cell(group, column.key)}</td>)}
                  <td className="p-2 text-right font-semibold text-gray-900">{group.total}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </FloatingScrollArea>
    </section>
  );
}
